import type { HelpCenterAudience } from "../../interfaces/models/IHelpCenter";

export const HELP_CENTER_NEW_SECTION_ROUTE_SEGMENT = "new";

/**
 * Both portals are served by the same `/help-center` route (declared in `MemberRoutes`), because
 * React Router matches on the pathname and cannot key a route on the query string. The staff
 * portal is `/help-center?portal=staff`; everything that depends on the portal (page content,
 * side menu, staff mode) must read the query, not the path.
 */
export const HELP_CENTER_PATH = "/help-center";
export const HELP_CENTER_STAFF_PORTAL_QUERY = "portal";
export const HELP_CENTER_STAFF_PORTAL_VALUE = "staff";
export const HELP_CENTER_STAFF_PATH = `${HELP_CENTER_PATH}?${HELP_CENTER_STAFF_PORTAL_QUERY}=${HELP_CENTER_STAFF_PORTAL_VALUE}`;

/** Matches only the Help Center landing route, not `/help-center/manage` or category pages. */
export function isHelpCenterRootPath(pathname: string): boolean {
  return pathname.toLowerCase().replace(/\/+$/, "") === HELP_CENTER_PATH;
}

export function hasHelpCenterStaffPortalQuery(search: string): boolean {
  return (
    new URLSearchParams(search).get(HELP_CENTER_STAFF_PORTAL_QUERY) ===
    HELP_CENTER_STAFF_PORTAL_VALUE
  );
}

export const HELP_CENTER_AUDIENCE_ROUTE: Record<HelpCenterAudience, string> = {
  members: "members",
  dataAdmin: "data-admin",
  staff: "staff",
};

const ROUTE_TO_AUDIENCE: Record<string, HelpCenterAudience> = {
  members: "members",
  "data-admin": "dataAdmin",
  staff: "staff",
};

export function audienceFromRouteParam(
  value: string | undefined,
): HelpCenterAudience | null {
  if (!value) {
    return null;
  }
  return ROUTE_TO_AUDIENCE[value] ?? null;
}

export function audienceToRouteParam(audience: HelpCenterAudience): string {
  return HELP_CENTER_AUDIENCE_ROUTE[audience];
}

export function helpCenterCategoryPath(
  audience: HelpCenterAudience,
  sectionId: string,
): string {
  return `/help-center/${audienceToRouteParam(audience)}/${sectionId}`;
}

export function helpCenterCategoryPathWithSubSection(
  audience: HelpCenterAudience,
  sectionId: string,
  subSectionId: string,
): { pathname: string; state: { subsectionId: string } } {
  return {
    pathname: helpCenterCategoryPath(audience, sectionId),
    state: { subsectionId: subSectionId },
  };
}

export function helpCenterCategoryEditPath(
  audience: HelpCenterAudience,
  sectionId: string,
): string {
  return `/help-center/manage/${audienceToRouteParam(audience)}/${sectionId}`;
}

export function helpCenterCategoryEditNewPath(
  audience: HelpCenterAudience,
): string {
  return helpCenterCategoryEditPath(
    audience,
    HELP_CENTER_NEW_SECTION_ROUTE_SEGMENT,
  );
}

export function isHelpCenterNewSectionRouteSegment(sectionId: string): boolean {
  return sectionId.toLowerCase() === HELP_CENTER_NEW_SECTION_ROUTE_SEGMENT;
}

export function helpCenterManagePath(audience: HelpCenterAudience): string {
  return `/help-center/manage/${audienceToRouteParam(audience)}`;
}

export function isHelpCenterManagePath(pathname: string): boolean {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);
  return segments[0] === "help-center" && segments[1] === "manage";
}

export function shouldSkipHelpCenterManageBreadcrumbSegment(
  pathSegments: string[],
  index: number,
): boolean {
  if (pathSegments[0]?.toLowerCase() !== "help-center") {
    return false;
  }

  if (pathSegments[1]?.toLowerCase() !== "manage") {
    return false;
  }

  return index >= 2;
}

export function getHelpCenterManageBreadcrumbActiveIndex(
  pathSegments: string[],
): number | null {
  if (pathSegments[0]?.toLowerCase() !== "help-center") {
    return null;
  }

  if (pathSegments[1]?.toLowerCase() !== "manage") {
    return null;
  }

  return 1;
}

const HELP_CENTER_AUDIENCE_ROUTE_SEGMENTS = new Set(
  Object.values(HELP_CENTER_AUDIENCE_ROUTE).map((segment) => segment.toLowerCase()),
);

export function isHelpCenterAudienceRouteSegment(segment: string): boolean {
  return HELP_CENTER_AUDIENCE_ROUTE_SEGMENTS.has(segment.toLowerCase());
}

export function isHelpCenterSubSectionRouteSegment(segment: string): boolean {
  const lower = segment.toLowerCase();
  return lower.startsWith("subsection-");
}

export function shouldSkipHelpCenterCategoryBreadcrumbSegment(
  pathSegments: string[],
  segment: string,
  index: number,
): boolean {
  if (pathSegments[0]?.toLowerCase() !== "help-center") {
    return false;
  }

  if (pathSegments[1]?.toLowerCase() === "manage") {
    return false;
  }

  if (isHelpCenterAudienceRouteSegment(segment)) {
    return true;
  }

  return index >= 3 && isHelpCenterSubSectionRouteSegment(segment);
}

export function getHelpCenterCategoryBreadcrumbActiveIndex(
  pathSegments: string[],
): number | null {
  if (pathSegments[0]?.toLowerCase() !== "help-center") {
    return null;
  }

  if (pathSegments[1]?.toLowerCase() === "manage") {
    return null;
  }

  if (pathSegments.length === 1) {
    return 0;
  }

  if (pathSegments.length >= 3) {
    return 2;
  }

  return null;
}

export function isHelpCenterCategoryPath(pathname: string): boolean {
  const segments = pathname.toLowerCase().split("/").filter(Boolean);
  return (
    segments[0] === "help-center" &&
    segments[1] !== "manage" &&
    segments.length >= 3
  );
}
